import { useEffect, useMemo, useState } from 'react'
import { Button } from 'shared/ui/button'
import { Card, CardContent } from 'shared/ui/card'
import { toast } from 'sonner'
import { WorkflowMetadataPanel } from './WorkflowMetadataPanel'
import { WorkflowRulesetSection } from './WorkflowRulesetSection'
import { WorkflowStageSection } from './WorkflowStageSection'
import { WorkflowPreviewPanel } from './WorkflowPreviewPanel'
import type {
  WorkflowMetadata,
  WorkflowRuleset,
  WorkflowStage,
  WorkflowTemplateOption,
  WorkflowStatusOption,
} from 'modules/admin/types/workflowTypes'
import {
  getWorkflowTemplates,
  getWorkflowConfiguration,
  getWorkflowStatuses,
  updateWorkflowConfiguration,
  type UpdateWorkflowConfigurationRequestDto,
} from 'modules/admin/api/workflowConfigurationService'
import { DEFAULT_TENANT_ID } from 'shared/utils/tenantContext'

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2)
}

interface WorkflowDesignerProps {
  currentUser?: {
    name?: string | null
    tenantId?: string | null
  }
  initialWorkflowVersionId?: string | null
  onBack?: () => void
}

export function WorkflowDesigner({ currentUser, initialWorkflowVersionId, onBack }: WorkflowDesignerProps) {
  const tenantId = currentUser?.tenantId ?? DEFAULT_TENANT_ID

  const [templates, setTemplates] = useState<WorkflowTemplateOption[]>([])
  const [selectedWorkflowVersionId, setSelectedWorkflowVersionId] = useState<string | null>(
    initialWorkflowVersionId ?? null,
  )
  const [metadata, setMetadata] = useState<WorkflowMetadata | null>(null)
  const [stages, setStages] = useState<WorkflowStage[]>([])
  const [rulesets, setRulesets] = useState<WorkflowRuleset[]>([])
  const [statusOptions, setStatusOptions] = useState<WorkflowStatusOption[]>([])
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (initialWorkflowVersionId === undefined) {
      return
    }
    setSelectedWorkflowVersionId(initialWorkflowVersionId)
  }, [initialWorkflowVersionId])

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.workflowVersionId === selectedWorkflowVersionId) ?? null,
    [templates, selectedWorkflowVersionId],
  )

  useEffect(() => {
    let mounted = true
    getWorkflowTemplates(tenantId)
      .then((items) => {
        if (!mounted) return
        const options: WorkflowTemplateOption[] = items.map((item) => ({
          id: item.workflowVersionId,
          workflowId: item.workflowId,
          workflowVersionId: item.workflowVersionId,
          templateId: item.templateId,
          templateVersionId: item.templateVersionId,
          tenantId: item.tenantId,
          name: item.templateName,
          siteName: item.siteName,
          permitType: item.permitCode,
          version: `v${item.workflowVersion}`,
          status: item.workflowIsActive ? 'ACTIVE' : 'INACTIVE',
          priority: item.workflowPriority,
        }))
        setTemplates(options)
        if (options.length > 0 && !selectedWorkflowVersionId) {
          setSelectedWorkflowVersionId(options[0].workflowVersionId)
        }
      })
      .catch(() => {
        toast.error('ไม่สามารถโหลด workflow templates ได้')
      })
    return () => {
      mounted = false
    }
  }, [tenantId, selectedWorkflowVersionId])

  useEffect(() => {
    let cancelled = false
    if (!tenantId) {
      setStatusOptions([])
      return
    }
    getWorkflowStatuses(tenantId)
      .then((items) => {
        if (cancelled) return
        setStatusOptions(items)
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('ไม่สามารถโหลดรายการสถานะของ Workflow ได้')
          setStatusOptions([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    const workflowVersionId = selectedWorkflowVersionId
    if (!workflowVersionId) {
      setMetadata(null)
      setStages([])
      setRulesets([])
      return
    }
    let mounted = true
    setIsDetailLoading(true)
    getWorkflowConfiguration(workflowVersionId)
      .then((detail) => {
        if (!mounted) return
        setMetadata({
          workflowId: detail.metadata.workflowId,
          workflowVersionId: detail.metadata.workflowVersionId,
          templateId: detail.metadata.templateId,
          templateVersionId: detail.metadata.templateVersionId,
          tenantId: detail.metadata.tenantId,
          workflowName: detail.metadata.workflowName,
          workflowCode: detail.metadata.workflowCode,
          version: detail.metadata.version,
          priority: detail.metadata.priority,
          description: detail.metadata.description ?? '',
          isActive: detail.metadata.isActive,
          capStageId: detail.metadata.capStageId ?? undefined,
          resubmitPolicy: detail.metadata.resubmitPolicy ?? 'RESTART',
          finalStatusCode: detail.metadata.finalStatusCode ?? null,
        })
        setStages(
          (detail.stages ?? []).map((stage) => ({
            stageId: stage.stageId,
            workflowVersionId: stage.workflowVersionId,
            code: stage.code,
            name: stage.name,
            seqOrder: stage.seqOrder,
            roleId: stage.roleId ?? '',
            roleName: stage.roleName ?? '',
            fieldId: stage.fieldId ?? '',
            fieldName: stage.fieldName ?? '',
            statusCode: stage.statusCode ?? undefined,
            statusName: stage.statusName ?? stage.statusCode ?? '',
            isGate: stage.isGate,
            onMissingApprover: stage.onMissingApprover ?? 'FAIL',
          })),
        )
        setRulesets(
          (detail.rulesets ?? []).map((ruleset) => ({
            workflowRulesetId: ruleset.workflowRulesetId,
            workflowVersionId: ruleset.workflowVersionId,
            name: ruleset.name,
            description: ruleset.description ?? '',
            capStageId: ruleset.capStageId,
            excludeStageIds: ruleset.excludeStageIds ?? [],
            priority: ruleset.priority,
            conditions: (ruleset.conditions ?? []).map((condition) => ({
              workflowRuleConditionId: condition.workflowRuleConditionId,
              workflowRulesetId: condition.workflowRulesetId,
              fieldId: condition.fieldId,
              fieldCode: condition.fieldCode ?? '',
              operator: condition.operator,
              value: condition.value ?? '',
            })),
          })),
        )
      })
      .catch(() => {
        toast.error('โหลด workflow configuration ไม่สำเร็จ')
        setMetadata(null)
        setStages([])
        setRulesets([])
      })
      .finally(() => {
        if (mounted) {
          setIsDetailLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [selectedWorkflowVersionId])

  const handleAddRuleset = () => {
    if (!selectedWorkflowVersionId) return
    const newRuleset: WorkflowRuleset = {
      workflowRulesetId: createId(),
      workflowVersionId: selectedWorkflowVersionId,
      name: 'New Ruleset',
      description: '',
      capStageId: stages[stages.length - 1]?.stageId,
      excludeStageIds: [],
      priority: rulesets.length * 10,
      conditions: [],
    }
    setRulesets((prev) => [...prev, newRuleset])
  }

  const handleUpdateRuleset = (rulesetId: string, changes: Partial<WorkflowRuleset>) => {
    setRulesets((prev) =>
      prev.map((ruleset) => (ruleset.workflowRulesetId === rulesetId ? { ...ruleset, ...changes } : ruleset)),
    )
  }

  const handleAddStage = () => {
    if (!metadata) return
    const nextSeq = stages.length + 1
    const newStage: WorkflowStage = {
      stageId: createId(),
      workflowVersionId: metadata.workflowVersionId,
      code: `S${nextSeq}`,
      name: `Stage ${nextSeq}`,
      seqOrder: nextSeq,
      roleId: '',
      roleName: '',
      fieldId: '',
      fieldName: '',
      statusCode: undefined,
      statusName: '',
      isGate: false,
      onMissingApprover: 'FAIL',
    }
    setStages((prev) => [...prev, newStage])
  }

  const handleUpdateStage = (stageId: string, changes: Partial<WorkflowStage>) => {
    setStages((prev) => prev.map((stage) => (stage.stageId === stageId ? { ...stage, ...changes } : stage)))
  }

  const handleSave = async () => {
    if (!selectedWorkflowVersionId || !metadata) {
      toast.error('ไม่พบข้อมูล workflow ที่เลือก')
      return
    }
    const sortedStages = [...stages]
      .sort((a, b) => a.seqOrder - b.seqOrder)
      .map((stage, index) => ({
        ...stage,
        seqOrder: index + 1,
      }))
    if (sortedStages.length === 0) {
      toast.error('ต้องมี stage อย่างน้อย 1 รายการ')
      return
    }
    const normalizedStagesWithStatus = sortedStages.map((stage) => {
      const trimmedStatus = stage.statusCode?.trim()
      const resolvedStatusName = trimmedStatus
        ? stage.statusName ||
          statusOptions.find((option) => option.statusCode === trimmedStatus)?.statusName ||
          trimmedStatus
        : undefined
      return {
        ...stage,
        statusCode: trimmedStatus || undefined,
        statusName: resolvedStatusName,
      }
    })
    const normalizedRulesets = rulesets.map((ruleset, index) => {
      const normalizedConditions = ruleset.conditions.filter((condition) => {
        return Boolean(condition.fieldId && condition.fieldId.trim().length > 0)
      })
      return {
        ...ruleset,
        priority: index * 10,
        capStageId: ruleset.capStageId ?? sortedStages[sortedStages.length - 1]?.stageId ?? '',
        excludeStageIds: Array.from(new Set((ruleset.excludeStageIds ?? []).filter(Boolean))),
        conditions: normalizedConditions,
      }
    })

    const payload: UpdateWorkflowConfigurationRequestDto = {
      metadata: {
        ...metadata,
        capStageId: metadata.capStageId ?? null,
      },
      stages: normalizedStagesWithStatus.map((stage) => ({
        stageId: stage.stageId,
        workflowVersionId: stage.workflowVersionId,
        code: stage.code,
        name: stage.name,
        seqOrder: stage.seqOrder,
        isGate: stage.isGate,
        onMissingApprover: stage.onMissingApprover ?? 'FAIL',
        roleId: stage.roleId && stage.roleId.trim().length > 0 ? stage.roleId : null,
        roleName: stage.roleName && stage.roleName.trim().length > 0 ? stage.roleName : null,
        fieldId: stage.fieldId && stage.fieldId.trim().length > 0 ? stage.fieldId : null,
        fieldName: stage.fieldName && stage.fieldName.trim().length > 0 ? stage.fieldName : null,
        statusCode: stage.statusCode ?? null,
        statusName: stage.statusName ?? null,
      })),
      rulesets: normalizedRulesets.map((ruleset) => ({
        workflowRulesetId: ruleset.workflowRulesetId,
        workflowVersionId: ruleset.workflowVersionId,
        name: ruleset.name,
        description: ruleset.description ?? '',
        capStageId: ruleset.capStageId ?? sortedStages[sortedStages.length - 1]?.stageId ?? '',
        excludeStageIds: ruleset.excludeStageIds ?? [],
        priority: ruleset.priority,
        conditions: ruleset.conditions.map((condition) => ({
          workflowRuleConditionId: condition.workflowRuleConditionId,
          workflowRulesetId: condition.workflowRulesetId,
          fieldId: condition.fieldId,
          fieldCode: condition.fieldCode ?? '',
          operator: condition.operator,
          value: condition.value ?? '',
        })),
      })),
    }

    setIsSaving(true)
    try {
      const result = await updateWorkflowConfiguration(selectedWorkflowVersionId, payload)
      toast.success('บันทึก Workflow สำเร็จ')
      setMetadata({
        workflowId: result.metadata.workflowId,
        workflowVersionId: result.metadata.workflowVersionId,
        templateId: result.metadata.templateId,
        templateVersionId: result.metadata.templateVersionId,
        tenantId: result.metadata.tenantId,
        workflowName: result.metadata.workflowName,
        workflowCode: result.metadata.workflowCode,
        version: result.metadata.version,
        priority: result.metadata.priority,
        description: result.metadata.description ?? '',
        isActive: result.metadata.isActive,
        capStageId: result.metadata.capStageId ?? undefined,
        resubmitPolicy: result.metadata.resubmitPolicy ?? 'RESTART',
        finalStatusCode: result.metadata.finalStatusCode ?? null,
      })
      setStages(
        result.stages.map((stage) => ({
          stageId: stage.stageId,
          workflowVersionId: stage.workflowVersionId,
          code: stage.code,
          name: stage.name,
          seqOrder: stage.seqOrder,
          roleId: stage.roleId ?? '',
          roleName: stage.roleName ?? '',
          fieldId: stage.fieldId ?? '',
          fieldName: stage.fieldName ?? '',
          statusCode: stage.statusCode ?? undefined,
          statusName: stage.statusName ?? stage.statusCode ?? '',
          isGate: stage.isGate,
          onMissingApprover: stage.onMissingApprover ?? 'FAIL',
        })),
      )
      setRulesets(
        result.rulesets.map((ruleset) => ({
          workflowRulesetId: ruleset.workflowRulesetId,
          workflowVersionId: ruleset.workflowVersionId,
          name: ruleset.name,
          description: ruleset.description ?? '',
          capStageId: ruleset.capStageId,
          excludeStageIds: ruleset.excludeStageIds ?? [],
          priority: ruleset.priority,
          conditions: (ruleset.conditions ?? []).map((condition) => ({
            workflowRuleConditionId: condition.workflowRuleConditionId,
            workflowRulesetId: condition.workflowRulesetId,
            fieldId: condition.fieldId,
            fieldCode: condition.fieldCode ?? '',
            operator: condition.operator,
            value: condition.value ?? '',
          })),
        })),
      )
    } catch (error) {
      toast.error('บันทึก Workflow ล้มเหลว')
    } finally {
      setIsSaving(false)
    }
  }

  const canRenderDetail = Boolean(metadata) && !isDetailLoading

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {onBack ? (
            <Button variant="link" className="px-0 text-blue-600" onClick={onBack}>
              ← กลับไปหน้า Workflow List
            </Button>
          ) : null}
          <h1 className="text-xl font-semibold text-gray-900">Workflow Configuration</h1>
          <p className="text-sm text-gray-500">จัดการ ruleset และ stage สำหรับ template แต่ละประเภท</p>
        </div>
        <Button onClick={handleSave} disabled={isSaving || !metadata}>
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
      <div className="space-y-4">
        {canRenderDetail ? (
          <>
          <WorkflowMetadataPanel
            metadata={metadata!}
            onChange={setMetadata}
            template={selectedTemplate}
            templates={templates}
            selectedWorkflowVersionId={selectedWorkflowVersionId}
            onTemplateChange={(workflowVersionId) => setSelectedWorkflowVersionId(workflowVersionId)}
            statusOptions={statusOptions}
          />
          <WorkflowRulesetSection
            rulesets={rulesets}
            stages={stages}
            templateVersionId={metadata?.templateVersionId ?? ''}
            onAddRuleset={handleAddRuleset}
            onUpdateRuleset={handleUpdateRuleset}
          />
          <WorkflowStageSection
            stages={stages}
            onAddStage={handleAddStage}
            onUpdateStage={handleUpdateStage}
            tenantId={metadata?.tenantId ?? ''}
            statusOptions={statusOptions}
          />
              <WorkflowPreviewPanel metadata={metadata!} stages={stages} rulesets={rulesets} />
          </>
        ) : (
          <Card>
            <CardContent className="py-10 text-center text-sm text-gray-500">
              {isDetailLoading ? 'กำลังโหลด workflow configuration...' : 'ไม่พบข้อมูล workflow ที่เลือก'}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default WorkflowDesigner
